import {Injectable} from '@angular/core';
import {HttpClient, HttpEvent, HttpHeaders} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {RefreshSearchResponse, SearchVisualsResponse, GamingResponse} from './models/api-response.models';

@Injectable({
  providedIn: 'root'
})
export class MypartHttpService {
  readonly httpPostOptions = {
    headers: new HttpHeaders({'Content-Type': 'application/x-www-form-urlencoded'}),
    withCredentials: true,
    observe: 'response' as 'response'
  };
  readonly httpPostOptionsWithProgress = {
    withCredentials: true,
    reportProgress: true,
    observe: 'events' as const // Changed to 'events' to get all event types
  };

  constructor(private httpClient: HttpClient) {
  }

  /**
   * Performs background login with hardcoded credentials
   * @returns Observable that emits the login response
   */
  login(): Observable<any> {
    const username = 'v2sdemo@demo.com';
    const password = 'v2sdemo';
    const params = this.param({username, password});

    // Using the environment variable from environment.ts
    return this.httpClient.post<any>(
      `${environment.NG_ENV_BASE_API}auth/login`,
      params,
      this.httpPostOptions
    ).pipe(
      tap(response => {
        if (response.body) {
          // Set JWT token first
          localStorage.setItem('jwt_token', (response.body as any).data.jwt);
        }
        console.log('Login successful');
      }),
      catchError(error => {
        console.error('Login failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Submits a lead-gen / contact form
   * @param email User's email address
   * @param formType Form identifier (e.g. "V2S_CONTACT")
   * @returns Observable that emits the send-form response
   */
  sendForm(email: string, formType: string): Observable<any> {
    const params = this.param({email, formType});

    return this.httpClient.post<any>(
      `${environment.NG_ENV_BASE_API}auth/send-form`,
      params,
      this.httpPostOptions
    ).pipe(
      catchError(error => {
        console.error('Send form failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Unified search function that determines which specialized search function to call
   * based on the input files.
   *
   * @param files Single file or array of files to upload
   * @param numResults Number of results to return (default 10)
   * @param searchResolution Set to "section" for specific song sections, omit for full songs only
   * @param text Additional description or requests (optional)
   * @returns Observable with HTTP events (progress, response, etc.)
   */
  searchVisuals(files: File[], numResults: number = 10, searchResolution: string = 'section', text?: string): Observable<HttpEvent<SearchVisualsResponse>> {
    // Determine which search function to call based on the input
    if (files.length > 1) {
      // Multiple files - use collection search
      return this.searchVisualsCollection(files, numResults, searchResolution, text);
    } else {
      // Single file - determine if it's a video or image
      const file = files[0];

      if (file.type.startsWith('video/')) {
        // Video file
        return this.searchVisualsVideo(file, numResults, searchResolution, text);
      } else {
        // Image file (default)
        return this.searchVisualsImage(file, numResults, searchResolution, text);
      }
    }
  }

  /**
   * Search for audio tracks using free text query
   * @param query Text of the search query
   * @param numResults Number of results to return (default 10)
   * @param searchResolution Set to "section" for specific song sections, omit for full songs only
   * @returns Observable with HTTP events (progress, response, etc.)
   */
  refreshSearch(query: string, numResults: number = 10, searchResolution: string = 'song'): Observable<HttpEvent<RefreshSearchResponse>> {
    // Create FormData object for proper parameter handling
    const formData = new FormData();

    // Add required parameters
    formData.append('query', query);
    formData.append('num_results', numResults.toString());
    formData.append('search_resolution', searchResolution);

    // Get JWT token and create headers
    const jwt = localStorage.getItem('jwt_token');
    const headers = new HttpHeaders(jwt ? {
      'Authorization': `Bearer ${jwt}`
    } : {});

    // Execute the request
    return this.httpClient.post<RefreshSearchResponse>(
      `${environment.NG_ENV_BASE_API}search/free-text`,
      formData,
      {
        headers,
        withCredentials: true,
        observe: 'events',
        reportProgress: true
      }
    );
  }

  /**
   * Search for visuals in a collection based on provided files
   * @param files List of File objects to upload
   * @param numResults Number of results to return (default 10)
   * @param searchResolution Set to "section" for specific song sections, omit for full songs only
   * @param text Additional description or requests (optional)
   * @returns Observable with HTTP events (progress, response, etc.)
   * @private Used internally by searchVisuals
   */
  private searchVisualsCollection(files: File[], numResults: number = 10, searchResolution: string = 'section', text?: string): Observable<HttpEvent<SearchVisualsResponse>> {
    return this.makeSearchRequest('collection', files, numResults, searchResolution, text);
  }

  /**
   * Search for visuals using a single image file
   * @param file Single File object to upload
   * @param numResults Number of results to return (default 10)
   * @param searchResolution Set to "section" for specific song sections, omit for full songs only
   * @param text Additional description or requests (optional)
   * @returns Observable with HTTP events (progress, response, etc.)
   * @private Used internally by searchVisuals
   */
  private searchVisualsImage(file: File, numResults: number = 10, searchResolution: string = 'section', text?: string): Observable<HttpEvent<SearchVisualsResponse>> {
    return this.makeSearchRequest('image', file, numResults, searchResolution, text);
  }

  /**
   * Search for visuals using a single video file
   * @param file Single File object to upload
   * @param numResults Number of results to return (default 10)
   * @param searchResolution Set to "section" for specific song sections, omit for full songs only
   * @param text Additional description or requests (optional)
   * @returns Observable with HTTP events (progress, response, etc.)
   * @private Used internally by searchVisuals
   */
  private searchVisualsVideo(file: File, numResults: number = 10, searchResolution: string = 'section', text?: string): Observable<HttpEvent<SearchVisualsResponse>> {
    return this.makeSearchRequest('video', file, numResults, searchResolution, text);
  }

  /**
   * Creates and executes a search request with the proper parameters and endpoint
   * @param searchType Type of search (collection, image, or video)
   * @param files File or array of files to upload
   * @param numResults Number of results to return
   * @param searchResolution Search resolution setting
   * @param text Optional text prompt
   * @returns Observable with HTTP events
   * @private
   */
  private makeSearchRequest(searchType: 'collection' | 'image' | 'video',
                            files: File | File[],
                            numResults: number,
                            searchResolution: string,
                            text?: string): Observable<HttpEvent<SearchVisualsResponse>> {
    // Create FormData object for proper file handling
    const formData = new FormData();

    // Handle file(s) based on search type
    if (searchType === 'collection') {
      // For collection search, append multiple files
      (files as File[]).forEach((file, index) => {
        const sanitizedName = this.sanitizeFilename(file.name, index);
        formData.append('files', file, sanitizedName);
      });
    } else {
      // For single file search (image or video)
      const file = files as File;
      const sanitizedName = this.sanitizeFilename(file.name, 0);
      formData.append('file', file, sanitizedName);
    }

    // Add common parameters
    formData.append('search_resolution', searchResolution);
    formData.append('numResults', numResults.toString());

    if (text && text.length > 0) {
      formData.append('text', text);
    }

    // Get JWT token and create headers
    const jwt = localStorage.getItem('jwt_token');
    const headers = new HttpHeaders(jwt ? {
      'Authorization': `Bearer ${jwt}`
    } : {});

    // Log file details to help debug progress events
    console.log(`Uploading ${searchType} with file size:`,
      searchType === 'collection' ?
        Array.from(files as File[]).map(f => `${f.name}: ${f.size} bytes`).join(', ') :
        `${(files as File).name}: ${(files as File).size} bytes`);

    // Ensure content type is NOT set to allow the browser to set the correct multipart boundary
    // Some headers can interfere with proper progress event reporting

    // Execute the request with explicit configuration for progress events
    return this.httpClient.post<SearchVisualsResponse>(
      `${environment.NG_ENV_BASE_API}search/visuals/${searchType}`,
      formData,
      {
        headers,
        withCredentials: true,
        observe: 'events',
        reportProgress: true,
        // Adding a custom timeout to ensure we have enough time to see upload progress
        // This is especially important for small files that might upload too quickly
        // Real timeout handling should be done at the subscription level
      }
    );
  }

  /**
   * Formats object parameters as application/x-www-form-urlencoded
   */
  /**
   * Sanitizes a filename to ensure it only contains English characters
   * @param filename Original filename
   * @param index Index of the file in the array (for generating a generic name if needed)
   * @returns Sanitized filename
   */
  private sanitizeFilename(filename: string, index: number): string {
    // If filename is empty or undefined, generate a generic name
    if (!filename) {
      return `file_${index}.jpg`;
    }

    // Get the file extension
    const lastDotIndex = filename.lastIndexOf('.');
    let extension = '';
    let baseName = filename;

    if (lastDotIndex !== -1) {
      extension = filename.substring(lastDotIndex).toLowerCase();
      baseName = filename.substring(0, lastDotIndex);
    }

    // Replace non-ASCII characters and invalid filename characters
    // This regex keeps only English letters, numbers, underscores, and hyphens
    const sanitized = baseName
      .replace(/[^\w\-]/g, '_') // Replace non-alphanumeric, non-underscore, non-hyphen with underscore
      .replace(/_{2,}/g, '_')    // Replace multiple underscores with a single one
      .substring(0, 50);         // Limit length of base filename

    // If sanitized name is empty, generate a generic name
    const finalName = sanitized || `file_${index}`;

    // Return with extension if it exists
    return finalName + extension;
  }

  /**
   * Search songs using image context (gaming mode)
   * @param file Image file to analyze for contextual search
   * @param text Optional text describing user preferences (e.g. "rock songs")
   * @param context Optional text describing game being played (e.g. "Minecraft")
   * @returns Observable of GamingResponse
   */
  searchContextualImage(file: File, text?: string, context?: string): Observable<GamingResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // Add optional parameters
    if (text) {
      formData.append('text', text);
    }
    if (context) {
      formData.append('context', context);
    }

    // Get JWT token and create headers
    const jwt = localStorage.getItem('jwt_token');
    const headers = new HttpHeaders(jwt ? {
      'Authorization': `Bearer ${jwt}`
    } : {});

    return this.httpClient.post<GamingResponse>(
      `${environment.NG_ENV_BASE_API}search/contextual/image`,
      formData,
      {
        headers,
        withCredentials: true
      }
    ).pipe(
      catchError(error => {
        console.error('Contextual image search failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Search songs using video context (gaming mode)
   * @param file Video file to analyze for contextual search
   * @param text Optional text describing user preferences (e.g. "rock songs")
   * @param context Optional text describing game being played (e.g. "Minecraft")
   * @returns Observable of GamingResponse
   */
  searchContextualVideo(file: File, text?: string, context?: string): Observable<GamingResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // Add optional parameters
    if (text) {
      formData.append('text', text);
    }
    if (context) {
      formData.append('context', context);
    }

    // Get JWT token and create headers
    const jwt = localStorage.getItem('jwt_token');
    const headers = new HttpHeaders(jwt ? {
      'Authorization': `Bearer ${jwt}`
    } : {});

    return this.httpClient.post<GamingResponse>(
      `${environment.NG_ENV_BASE_API}search/contextual/video`,
      formData,
      {
        headers,
        withCredentials: true
      }
    ).pipe(
      catchError(error => {
        console.error('Contextual video search failed:', error);
        return throwError(() => error);
      })
    );
  }

  private param(obj: any): string {
    const params = new URLSearchParams();

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        params.set(key, obj[key]);
      }
    }

    return params.toString();
  }
}
